import * as React from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import { debounce, isNil, noop } from 'lodash';

import { terminateSession } from 'modules/access/reducers/session';
import { notification } from 'redux-store/reducers/common-reducer';

import { __t, applyTranslations } from 'utils/language';
import { TEMPORARY_SESSION_STORAGE_KEY } from 'utils/session';
import { getItem, removeItem, setItem } from 'utils/storage/localStorage';

import type { NotificationType } from 'types';

applyTranslations(require('./temporary-session-provider.i18n.json'));

type TerminateSession = () => void;

type Notification = (
  d: NotificationType,
  c: string,
  b: boolean,
  a?: ((element: HTMLElement) => void) | null
) => void;

type DispatchProps = {
  dispatchTerminateSession: TerminateSession,
  dispatchNotification: Notification
};

type OwnProps = {};
type Props = OwnProps & DispatchProps;

const DEBOUNCE_DELAY = 250;
const NUM_MINUTES_UNTIL_LOGOUT = 15;
const NUM_MINUTES_UNTIL_WARNING = 5;
const INTERVAL_TIME = 60 * 1000; // every 1 minute
const MOUSE_MOVE = 'mousemove';
const KEYPRESS = 'keypress';

let INTERVAL_ID: number;

const msgKey = 'temporary_session_provider';

class TemporarySessionProvider extends React.PureComponent<Props> {
  debounceMethod = noop;
  notificationCloseRef: HTMLElement | undefined | null = null;

  constructor(props: Props) {
    super(props);

    this.debounceMethod = debounce(this.handleInteraction, DEBOUNCE_DELAY);
  }

  componentDidMount() {
    this.handleAddEventListeners();
    this.handleStorageItem();
    this.handleCheckInterval();
  }

  componentWillUnmount() {
    this.handleClearInterval();
    this.handleRemoveEventListeners();
  }

  handleCloseButtonRefCallback = (ref: HTMLElement): void => {
    this.notificationCloseRef = ref;
  };

  handleAddEventListeners(): void {
    document.addEventListener(MOUSE_MOVE, this.debounceMethod);
    document.addEventListener(KEYPRESS, this.debounceMethod);
  }

  handleRemoveEventListeners(): void {
    document.removeEventListener(MOUSE_MOVE, this.debounceMethod);
    document.removeEventListener(KEYPRESS, this.debounceMethod);
  }

  handleInteraction = (): void => {
    this.handleStorageItem(true);

    if (this.notificationCloseRef) {
      this.notificationCloseRef.click();
    }
  };

  handleStorageItem(reset: boolean = false): void {
    const storedTimestamp = getItem(TEMPORARY_SESSION_STORAGE_KEY);
    const updatedTimestamp = reset || isNil(storedTimestamp)
      ? Date.now()
      : storedTimestamp;

    setItem(TEMPORARY_SESSION_STORAGE_KEY, String(updatedTimestamp));
  }

  handleDispatchWarning(numMinutesWarning: number): void {
    const { dispatchNotification } = this.props;

    const msg = __t(`${msgKey}.messages.will_expire`, {
      numMinutes: numMinutesWarning,
      withPlural: numMinutesWarning
    });

    dispatchNotification('warning', msg, false, this.handleCloseButtonRefCallback);
  }

  handleSessionExpired(): void {
    const { dispatchTerminateSession, dispatchNotification } = this.props;

    this.handleClearInterval();

    removeItem(TEMPORARY_SESSION_STORAGE_KEY);

    dispatchTerminateSession();
    dispatchNotification('info', __t(`${msgKey}.messages.has_expired`), true);
  }

  handleClearInterval() {
    window.clearInterval(INTERVAL_ID);
  }

  handleCheckInterval(): void {
    INTERVAL_ID = window.setInterval(() => {
      const storedTimestamp = Number(getItem(TEMPORARY_SESSION_STORAGE_KEY));
      const now = Date.now();
      const numMinutesSinceStorage = moment(now).diff(moment(storedTimestamp), 'minutes');
      const numMinutesWarning = NUM_MINUTES_UNTIL_LOGOUT - numMinutesSinceStorage;

      if (numMinutesWarning <= NUM_MINUTES_UNTIL_WARNING) {
        this.handleDispatchWarning(numMinutesWarning);
      }

      if (numMinutesSinceStorage >= NUM_MINUTES_UNTIL_LOGOUT) {
        this.handleSessionExpired();
      }
    }, INTERVAL_TIME);
  }

  render(): null {
    return null;
  }
}

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchTerminateSession: () => dispatch(terminateSession()),

  dispatchNotification: (
    type: NotificationType,
    message: string,
    keepBetweenPages: boolean,
    closeButtonRefCallback
  ) => (
    dispatch(notification({
      type,
      message,
      keepBetweenPages,
      closeButtonRefCallback
    }))
  )
});

export default connect(null, mapDispatchToProps)(TemporarySessionProvider);
